import type { EmployeeStatusChipStatus } from "../../../../components/UIToolkit/Badges/EmployeeStatusChip";
import { formatPersonFullName } from "../../../../utils/formatters";
import type { MergeMembership, MergeSide, NurseMergeDecisions, NurseMergePreview } from "../../../../utils/nurseMergeModels";

/**
 * What a person reads in place of the backend's `"table.column"` move counts. Each group names one thing
 * staff and admins recognise; several columns can feed one group (time off lives in two tables).
 */
export type MovedGroupId =
	| "scheduledShifts"
	| "actualShifts"
	| "timeOffRequests"
	| "timeOff"
	| "recurringShifts"
	| "hrisLinks"
	| "hrisShifts"
	| "pbjHours"
	| "shiftRequests"
	| "qualifications"
	| "changeHistory";

/** Ordered by how much a scheduler cares: the schedule first, bookkeeping last. */
export const MOVED_COUNT_GROUPS: ReadonlyArray<{ id: MovedGroupId; keys: readonly string[] }> = [
	{ id: "scheduledShifts", keys: ["nurse_in_shift.nurse_id"] },
	{ id: "actualShifts", keys: ["nurse_in_shift_actual.nurse_id"] },
	{ id: "timeOffRequests", keys: ["time_off_request.requesting_staff_id"] },
	{ id: "timeOff", keys: ["employee_specific_preferences.nurse_id", "external_time_off.nurse_id"] },
	{ id: "recurringShifts", keys: ["recurring_shift.nurse_id"] },
	{ id: "hrisLinks", keys: ["external_employee.nurse_id"] },
	{ id: "hrisShifts", keys: ["external_shift.nurse_id"] },
	{ id: "pbjHours", keys: ["pbj_shift.nurse_id", "pbj_manual_hours_entry.nurse_id"] },
	{
		id: "shiftRequests",
		keys: [
			"requests.request_for",
			"requests_status.nurse_id",
			"agency_requests_status.nurse_id",
			"shift_pickup_agent_nurse_actions.nurse_id",
			"swap_requests.requested_nurse_id",
		],
	},
	{ id: "qualifications", keys: ["qualifications.nurse_id", "location_credentials.nurse_id"] },
	{ id: "changeHistory", keys: ["staff_change_log.nurse"] },
];

/**
 * Columns the summary deliberately does not count, each for a reason a person would not want spelled out:
 * - membership rows are what the department choices decide, shown there instead;
 * - wage history and weekly patterns are "kept only if the other record has none" — the warnings say so,
 *   and a "moves" count would contradict them;
 * - the rest are derived or legacy bookkeeping that is rebuilt or mirrors a table already counted.
 */
export const UNCOUNTED_MOVED_KEYS: ReadonlySet<string> = new Set([
	"nurse_department_status.nurse_id",
	"employee_status_decision.nurse_id",
	"nurse_base_wage.nurse_id",
	"employee_weekly_preferences.nurse_id",
	"wage_breakdown_rows_projected.nurse_id",
	"wage_recalc_queue.nurse_id",
	"pto_cost.nurse_id",
	"nurse_in_shift.replaced_with",
	"nurse_master_schedule.nurse_id",
	"nurse_absents.absent_nurse_id",
	"nurse_absents.replacement_nurse_id",
	"nurse_in_shift_actual2.nurse_id",
	"webclock_employees.nurse_id",
	"paycom_employees.nurse_id",
	"constraint.nurse_id",
	"constraints.nurse_id",
]);

export type MovedGroupCount = { id: MovedGroupId; count: number };

/**
 * Folds the backend's per-column move counts into the groups a person reads. Groups with nothing to move
 * are dropped. A column the app has no name for is not hidden: it lands in `otherCount`, so a new table the
 * merge starts moving still shows up as "N other records" rather than disappearing from the summary.
 */
export const groupMovedCounts = (movedCounts: Record<string, number>): { groups: MovedGroupCount[]; otherCount: number } => {
	const named = new Set(MOVED_COUNT_GROUPS.flatMap((group) => group.keys));
	const groups = MOVED_COUNT_GROUPS.map((group) => ({
		id: group.id,
		// DEFAULT: a column is absent when this database has no such table (legacy tables exist in prod only) — nothing moves.
		count: group.keys.reduce((sum, key) => sum + (movedCounts[key] ?? 0), 0),
	})).filter((group) => group.count > 0);
	const otherCount = Object.entries(movedCounts)
		.filter(([key]) => !named.has(key) && !UNCOUNTED_MOVED_KEYS.has(key))
		.reduce((sum, [, count]) => sum + count, 0);
	return { groups, otherCount };
};

/** A record's standing in one department, as the house status chip draws it. `null` = not a member there. */
export const membershipStatus = (membership: MergeMembership | undefined): EmployeeStatusChipStatus | null => {
	if (!membership) return null;
	if (!membership.active) return "Inactive";
	if (membership.status === "OnLeave" || membership.status === "Suspended") return membership.status;
	return "Active";
};

/**
 * The person's answers, keyed so they survive swapping which record is kept: a swap re-fetches the preview
 * with `primary`/`duplicate` reversed, but "keep them active at Main", "keep Jane #12's recurring schedule"
 * and "keep Jane #12's shifts" still mean the same thing afterwards.
 */
export type MergeChoices = {
	/** Per conflicting department: does the merged record stay active there? */
	keepActiveByDepartment: Record<string, boolean>;
	/** Per facility with two live recurring schedules: which employee's templates remain active. */
	recurringKeepNurseIdByDepartment: Record<string, number>;
	/** Whose shifts win where the two records' shifts overlap. */
	overlapKeepNurseId: number | null;
};

export const EMPTY_MERGE_CHOICES: MergeChoices = {
	keepActiveByDepartment: {},
	recurringKeepNurseIdByDepartment: {},
	overlapKeepNurseId: null,
};

/** How many of the choices this preview needs are still unanswered. */
export const pendingDecisionCount = (preview: NurseMergePreview, choices: MergeChoices): number => {
	const departments = preview.departmentConflicts.filter((conflict) => choices.keepActiveByDepartment[conflict.departmentId] === undefined).length;
	const recurring = preview.recurringConflicts.filter(
		(conflict) => !isNurseChoiceValid(preview, choices.recurringKeepNurseIdByDepartment[conflict.departmentId]),
	).length;
	const overlap = preview.shiftOverlaps.length > 0 && !isOverlapChoiceValid(preview, choices.overlapKeepNurseId) ? 1 : 0;
	return departments + recurring + overlap;
};

const isOverlapChoiceValid = (preview: NurseMergePreview, nurseId: number | null): boolean =>
	nurseId === preview.primary.nurseId || nurseId === preview.duplicate.nurseId;

const isNurseChoiceValid = (preview: NurseMergePreview, nurseId: number | undefined): boolean =>
	nurseId === preview.primary.nurseId || nurseId === preview.duplicate.nurseId;

/**
 * The request's `decisions`, or `null` while anything the preview asks for is unanswered. Carries exactly
 * the preview's membership and recurring-schedule conflict departments (the backend refuses extras) and an
 * overlap side only when there are overlaps (the backend refuses one otherwise).
 */
export const buildMergeDecisions = (preview: NurseMergePreview, choices: MergeChoices): NurseMergeDecisions | null => {
	if (pendingDecisionCount(preview, choices) > 0) return null;
	const departmentChoices: Record<string, MergeSide> = {};
	for (const conflict of preview.departmentConflicts) {
		const keepActive = choices.keepActiveByDepartment[conflict.departmentId];
		departmentChoices[conflict.departmentId] = conflict.primaryActive === keepActive ? "primary" : "duplicate";
	}
	const recurringChoices: Record<string, MergeSide> = {};
	for (const conflict of preview.recurringConflicts) {
		recurringChoices[conflict.departmentId] =
			choices.recurringKeepNurseIdByDepartment[conflict.departmentId] === preview.primary.nurseId ? "primary" : "duplicate";
	}
	const overlapKeep: MergeSide | null =
		preview.shiftOverlaps.length === 0 ? null : choices.overlapKeepNurseId === preview.primary.nurseId ? "primary" : "duplicate";
	return { departmentChoices, recurringChoices, overlapKeep };
};

/** Distinct shifts the losing side gives up where the two records overlap (one shift can overlap two). */
export const removedOverlapShiftCount = (preview: NurseMergePreview, keepNurseId: number): number => {
	const losingIsDuplicate = keepNurseId === preview.primary.nurseId;
	return new Set(preview.shiftOverlaps.map((overlap) => (losingIsDuplicate ? overlap.duplicateShiftId : overlap.primaryShiftId))).size;
};

/** The decide step is shown only when the merge asks something of the person or cannot go ahead. */
export const needsDecisionStep = (preview: NurseMergePreview): boolean =>
	preview.departmentConflicts.length > 0 || preview.recurringConflicts.length > 0 || preview.shiftOverlaps.length > 0 || preview.blockers.length > 0;

/** Name plus id — two duplicates usually share a name, so the id is what tells them apart. */
export const formatNurseLabel = (person: { firstName: string; lastName: string; nurseId: number }, lastNameFirst: boolean): string =>
	`${formatPersonFullName(person, lastNameFirst)} #${person.nurseId}`;

/**
 * The i18n key for an employment type as the backend spells it. Most values are already keys ("fullTime",
 * "perDiem"); PRN is the one whose label lives under a namespaced key.
 */
export const employmentTypeKey = (employmentType: string): string => (employmentType === "PRN" ? "employment.type.prn" : employmentType);
